import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useShallow } from 'zustand/shallow';
import { MeshBVH, acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from 'three-mesh-bvh';
import { useEditorStore, useThicknessStore, useWorkflowStore } from '../../../stores';
import { CreateThicknessPreviewMaterial, CreateTopMeshPreviewMaterial } from '../../../utils/PreviewMaterialsHandler';
import { useToolManager } from '../../../hooks/useToolManager';
import { TOOLS_CONFIG } from '../../../constants/tools';
import { generateTopMesh } from './topMeshGenerator';
import { findTopBoundaryVertices, findTopmostVertex, getBoundaryLoop } from './boundaryUtils';
import { createShellWithFaceNormals, computeSmartVertexNormals, smoothBoundaryVertices } from './shellUtils';
import { checkGeometryHealth } from '../flares/geometryValidation';
import { applyEasing, EASING_FUNCTION } from '../../../utils/EasingFunctions';
import Delaunator from 'delaunator';

// Ensure BVH-accelerated raycasting is available wherever this handler is used.
// This is idempotent and safe even if applied in other modules.
THREE.Mesh.prototype.raycast = acceleratedRaycast;
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;

// Units: scene uses centimeters. User input is millimeters.
// 1 mm = 0.1 scene units (cm)
const MM_TO_UNIT = 0.1;

// ============== HELPER FUNCTIONS FOR LAPLACIAN SMOOTHING ==============

/**
 * Build 1-ring adjacency for vertices from indexed geometry
 * Returns array where each index maps to a Uint32Array of neighbor vertex indices
 */
function buildAdjacency(geom) {
  const idx = geom.index ? geom.index.array : null;
  const vcount = geom.attributes.position.count;
  const nbrs = Array(vcount).fill(null).map(() => new Set());

  if (!idx) {
    // Generate triangles from sequential triplets if no index
    const n = Math.floor(vcount / 3) * 3;
    for (let i = 0; i < n; i += 3) {
      const a = i, b = i + 1, c = i + 2;
      nbrs[a].add(b).add(c);
      nbrs[b].add(a).add(c);
      nbrs[c].add(a).add(b);
    }
  } else {
    for (let i = 0; i < idx.length; i += 3) {
      const a = idx[i], b = idx[i + 1], c = idx[i + 2];
      nbrs[a].add(b).add(c);
      nbrs[b].add(a).add(c);
      nbrs[c].add(a).add(b);
    }
  }

  // Pack sets to typed arrays for speed
  return nbrs.map(s => Uint32Array.from(s));
}

/**
 * Detect boundary vertices (vertices on edges that belong to only one triangle)
 * Returns Uint8Array where 1 = boundary vertex, 0 = interior vertex
 */
function detectBoundaryVertices(geom) {
  const idx = geom.index ? geom.index.array : null;
  const vcount = geom.attributes.position.count;
  const isBoundary = new Uint8Array(vcount);

  const edgeCount = new Map();

  const addEdge = (a, b) => {
    const min = Math.min(a, b);
    const max = Math.max(a, b);
    const key = `${min}_${max}`;
    edgeCount.set(key, (edgeCount.get(key) || 0) + 1);
  };

  if (!idx) {
    const n = Math.floor(vcount / 3) * 3;
    for (let i = 0; i < n; i += 3) {
      const a = i, b = i + 1, c = i + 2;
      addEdge(a, b);
      addEdge(b, c);
      addEdge(c, a);
    }
  } else {
    for (let i = 0; i < idx.length; i += 3) {
      const a = idx[i], b = idx[i + 1], c = idx[i + 2];
      addEdge(a, b);
      addEdge(b, c);
      addEdge(c, a);
    }
  }

  for (const [key, count] of edgeCount) {
    if (count === 1) {
      const [a, b] = key.split('_').map(Number);
      isBoundary[a] = 1;
      isBoundary[b] = 1;
    }
  }

  return isBoundary;
}

/**
 * Apply offset to positions along their normals
 * Used to move inner surfaces outward for wearer comfort
 * @param {Float32Array} positions - Original positions to offset
 * @param {Float32Array} normals - Vertex normals for displacement direction
 * @param {number} offset - Offset distance (in scene units)
 * @returns {Float32Array} Offset positions
 */
function applyOffsetToPositions(positions, normals, offset) {
  if (offset <= 0 || !positions || !normals) {
    return positions;
  }

  const offsetPositions = new Float32Array(positions);
  const vertexCount = positions.length / 3;

  for (let i = 0; i < vertexCount; i++) {
    const i3 = i * 3;
    const nx = normals[i3];
    const ny = normals[i3 + 1];
    const nz = normals[i3 + 2];

    offsetPositions[i3] += nx * offset;
    offsetPositions[i3 + 1] += ny * offset;
    offsetPositions[i3 + 2] += nz * offset;
  }

  return offsetPositions;
}

/**
 * Apply Laplacian smoothing to outer surface vertices
 * Only smooths non-boundary vertices to preserve the rim shape
 */
function applySurfaceSmoothing(shellGeometry, outerVertexIndices, neighbors, isBoundary, iterations) {
  if (iterations <= 0 || !outerVertexIndices || outerVertexIndices.length === 0) {
    return;
  }

  const positions = shellGeometry.attributes.position.array;
  const curr = new Float32Array(positions);
  const tmp = new Float32Array(positions.length);

  for (let k = 0; k < iterations; k++) {
    // Copy all positions to tmp for this iteration
    tmp.set(curr);

    for (const i of outerVertexIndices) {
      // Skip boundary vertices completely - they should never be smoothed
      // This preserves the rim shape and prevents shape loss
      if (isBoundary && isBoundary[i]) {
        continue;
      }

      const i3 = i * 3;
      const ox = curr[i3];
      const oy = curr[i3 + 1];
      const oz = curr[i3 + 2];

      // Get neighbors from adjacency
      const nb = neighbors[i];
      if (!nb || nb.length === 0) {
        continue;
      }

      let sx = 0, sy = 0, sz = 0;
      let cnt = 0;

      // Sum positions of all neighbors
      for (let j = 0; j < nb.length; j++) {
        const jj = nb[j] * 3;
        sx += curr[jj];
        sy += curr[jj + 1];
        sz += curr[jj + 2];
        cnt++;
      }

      if (cnt === 0) {
        continue;
      }

      // Calculate average neighbor position
      const inv = 1 / cnt;
      const ax = sx * inv;
      const ay = sy * inv;
      const az = sz * inv;

      // Blend current position with neighbor average (full smoothing step)
      const alpha = 1.0;
      tmp[i3] = ox * (1 - alpha) + ax * alpha;
      tmp[i3 + 1] = oy * (1 - alpha) + ay * alpha;
      tmp[i3 + 2] = oz * (1 - alpha) + az * alpha;
    }

    curr.set(tmp);
  }

  // Update geometry positions
  positions.set(curr);
  shellGeometry.attributes.position.needsUpdate = true;
}

/**
 * Apply rim boundary smoothing to displaced positions before shell creation
 * Creates smooth easing transition from inner to outer surface at boundaries
 * @param {Float32Array} innerPositions - Original inner surface positions
 * @param {Float32Array} outerDisplacedPositions - Displaced outer surface positions (modified in place)
 * @param {number} vertexCount - Total vertex count
 * @param {Set} boundaryVertices - Set of boundary vertex indices
 * @param {Array} boundaryEdges - Array of boundary edges [a, b]
 * @param {number} smoothnessLength - Length of smoothing zone from boundary
 * @param {string} easingProfile - Easing function type
 * @param {number} minimumThickness - Minimum thickness to maintain at boundary (default: 0)
 */
function applyRimBoundarySmoothing(
  innerPositions,
  outerDisplacedPositions,
  vertexCount,
  boundaryVertices,
  boundaryEdges,
  smoothnessLength,
  easingProfile,
  minimumThickness = 0
) {
  if (!innerPositions || !outerDisplacedPositions || smoothnessLength <= 0 || boundaryVertices.size === 0) {
    return;
  }

  // Use spatial proximity approach instead of graph-based distance
  // For each boundary vertex, smooth nearby vertices using spatial distance

  // Pre-calculate boundary vertex positions for spatial queries
  const boundaryPositions = [];
  for (const boundaryIdx of boundaryVertices) {
    const i3 = boundaryIdx * 3;
    boundaryPositions.push({
      idx: boundaryIdx,
      x: innerPositions[i3],
      y: innerPositions[i3 + 1],
      z: innerPositions[i3 + 2]
    });
  }

  // For each vertex in the mesh, compute distance to nearest boundary vertex
  const distanceToBoundary = new Float32Array(vertexCount);
  distanceToBoundary.fill(Infinity);

  for (let i = 0; i < vertexCount; i++) {
    const i3 = i * 3;
    const x = innerPositions[i3];
    const y = innerPositions[i3 + 1];
    const z = innerPositions[i3 + 2];

    // Find minimum distance to any boundary vertex
    let minDist = Infinity;
    for (const boundaryPos of boundaryPositions) {
      const dx = x - boundaryPos.x;
      const dy = y - boundaryPos.y;
      const dz = z - boundaryPos.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist < minDist) {
        minDist = dist;
      }
    }
    distanceToBoundary[i] = minDist;
  }

  // Apply easing to outer displaced positions to create smooth beveled rim
  for (let i = 0; i < vertexCount; i++) {
    const spatialDist = distanceToBoundary[i];

    // Only affect vertices within smoothness zone (including boundary vertices)
    if (spatialDist >= 0 && spatialDist <= smoothnessLength) {
      // Normalize spatial distance to [0, 1]
      // At boundary (0): should reduce displacement to maintain minimum thickness
      // At smoothnessLength (1): should use full displacement
      const normalizedDist = Math.min(1.0, spatialDist / smoothnessLength);

      // Apply easing: creates smooth curve from reduced to full displacement
      // The easing profile controls the shape of the beveled edge
      const easedFactor = applyEasing(normalizedDist, easingProfile);

      const i3 = i * 3;
      const innerX = innerPositions[i3];
      const innerY = innerPositions[i3 + 1];
      const innerZ = innerPositions[i3 + 2];
      const outerX = outerDisplacedPositions[i3];
      const outerY = outerDisplacedPositions[i3 + 1];
      const outerZ = outerDisplacedPositions[i3 + 2];

      // Calculate original displacement vector
      const dispX = outerX - innerX;
      const dispY = outerY - innerY;
      const dispZ = outerZ - innerZ;
      const dispLength = Math.sqrt(dispX * dispX + dispY * dispY + dispZ * dispZ);

      // Blend between minimum thickness and full displacement
      // At boundary (easedFactor≈0): use minimum thickness
      // Away from boundary (easedFactor≈1): use full displacement
      let effectiveThickness = minimumThickness + (dispLength - minimumThickness) * easedFactor;

      // Clamp to valid range
      effectiveThickness = Math.max(minimumThickness, Math.min(dispLength, effectiveThickness));

      // Apply thickness while preserving displacement direction
      if (dispLength > 1e-6) {
        const scale = effectiveThickness / dispLength;
        outerDisplacedPositions[i3] = innerX + dispX * scale;
        outerDisplacedPositions[i3 + 1] = innerY + dispY * scale;
        outerDisplacedPositions[i3 + 2] = innerZ + dispZ * scale;
      }
    }
  }
}

const ThicknessHandler = () => {
  const [scene, shadedMesh, material, replaceShadedMesh] = useEditorStore(
    useShallow((s) => [
      s.sceneRefs.scene,
      s.sceneRefs.shadedMesh,
      s.sceneRefs.material,
      s.replaceShadedMesh,
    ])
  );

  const [thicknessState, previewMesh, setPreviewMesh, isThicknessApplied] = useThicknessStore(
    useShallow((s) => [s.thickness, s.previewMesh, s.setPreviewMesh, s.isThicknessApplied])
  );

  // Get selected socket type for mould mode detection
  const selectedSocketType = useWorkflowStore((s) => s.selectedSocketType);

  const { run } = useToolManager();

  // Preview mesh management
  const basePosRef = useRef(null); // Float32Array snapshot of pristine positions
  const baseGeomRef = useRef(null); // BufferGeometry snapshot
  const baseNormalsRef = useRef(null); // Float32Array snapshot of pristine normals
  const baseMaxThicknessRef = useRef(null); // per-vertex maximum safe thickness along normals
  const lastMeshRef = useRef(null); // to detect shadedMesh changes
  const previewMeshRef = useRef(null); // to detect previewMesh changes
  const originalMaterialRef = useRef(null); // shaded mesh original material for final replace
  const isInitialMountRef = useRef(true); // to prevent unapply logic on initial mount

  // Top mesh management for mould mode
  const topMeshRef = useRef(null); // cached top mesh geometry
  const topMeshPreviewRef = useRef(null); // top mesh preview object
  const lastSocketTypeRef = useRef(null); // to detect socket type changes
  const topMeshSolidRef = useRef(null); // solid top mesh attached to shadedMesh
  
  // Top lid management for mould mode (closing lid on top of upper mesh)
  const topLidRef = useRef(null); // cached top lid geometry
  const topLidPreviewRef = useRef(null); // top lid preview object
  const topLidSolidRef = useRef(null); // solid top lid attached to shadedMesh

  // Preview mesh creation and management
  function ensurePreview() {
    // Don't create preview if thickness is already applied
    if (isThicknessApplied) return null;

    if (previewMesh && previewMesh.parent) return previewMesh;
    if (previewMeshRef.current) return previewMeshRef.current;
    if (!scene || !shadedMesh) return null;

    // Base snapshots for stable rebuilds and to survive shadedMesh changes
    if (!baseGeomRef.current || lastMeshRef.current !== shadedMesh) {
      baseGeomRef.current = shadedMesh.geometry.clone();
      if (!baseGeomRef.current.attributes.position) return null;

      // Check geometry health before processing
      // const health = checkGeometryHealth(baseGeomRef.current);
      // if (!health.healthy) {
      //   console.warn('[ThicknessHandler] Base geometry has issues:', health.issues);
      // }

      basePosRef.current = baseGeomRef.current.attributes.position.array.slice();

      // Use area-weighted normals to prevent stepping artifacts
      // This gives more weight to larger faces, resulting in smoother displacement
      try {
        baseNormalsRef.current = computeSmartVertexNormals(baseGeomRef.current);
        console.log('[ThicknessHandler] Computed smart normals for', basePosRef.current.length / 3, 'vertices');
      } catch (error) {
        console.warn('[ThicknessHandler] Failed to compute smart normals, falling back to standard:', error);
        if (!baseGeomRef.current.attributes.normal) {
          baseGeomRef.current.computeVertexNormals();
        }
        baseNormalsRef.current = baseGeomRef.current.attributes.normal.array.slice();
      }

      // Pre-compute a per-vertex maximum safe thickness field to avoid self-intersections
      try {
        baseMaxThicknessRef.current = computePerVertexMaxThickness(
          baseGeomRef.current,
          basePosRef.current,
          baseNormalsRef.current
        );
      } catch (error) {
        console.warn('[ThicknessHandler] Failed to compute per-vertex max thickness field:', error);
        baseMaxThicknessRef.current = null;
      }

      lastMeshRef.current = shadedMesh;
      originalMaterialRef.current = material;
    }

    const cloneGeom = baseGeomRef.current.clone();
    const mat = CreateThicknessPreviewMaterial(0.5);
    const clone = new THREE.Mesh(cloneGeom, mat);
    clone.name = 'ThicknessPreview';
    clone.matrixAutoUpdate = true;
    clone.position.copy(shadedMesh.position);
    clone.quaternion.copy(shadedMesh.quaternion);
    clone.scale.copy(shadedMesh.scale);
    scene.add(clone);

    previewMeshRef.current = clone;
    setPreviewMesh(clone);
    return clone;
  }

  function disposePreview(options = { keepIfReplaced: false }) {
    const p = previewMeshRef.current;
    if (!p) return;
    if (options.keepIfReplaced) {
      // We are turning the preview into the shaded mesh; do not dispose or remove.
      setPreviewMesh(null);
      return;
    }
    if (p.parent) p.parent.remove(p);
    p.geometry?.dispose?.();
    p.material?.dispose?.();
    setPreviewMesh(null);
    previewMeshRef.current = null;
  }

  // Top mesh creation and management for mould mode
  function ensureTopMesh() {
    // Don't create top mesh if thickness is already applied
    if (isThicknessApplied) return null;

    const isMouldMode = selectedSocketType === 'mould';
    if (!isMouldMode) return null;

    // Check if we need to regenerate the top mesh
    // Regenerate if: no cached mesh, shadedMesh changed, socket type changed, or geometry was modified
    const geometryChanged = lastMeshRef.current !== shadedMesh ||
                           (shadedMesh && lastMeshRef.current &&
                            shadedMesh.geometry !== lastMeshRef.current.geometry);

    if (!topMeshRef.current || geometryChanged || lastSocketTypeRef.current !== selectedSocketType) {

      if (!baseGeomRef.current) {
        return null;
      }

      // Generate top mesh from current geometry with debug scene
      const topMeshGeometry = generateTopMesh(shadedMesh.geometry, 2, {
        // debugScene: scene  // Pass scene for debug visualization
      });

      if (!topMeshGeometry) {
        return null;
      }

      // Cache the generated mesh and update references
      topMeshRef.current = topMeshGeometry;
      lastSocketTypeRef.current = selectedSocketType;
      lastMeshRef.current = shadedMesh;

      // Dispose old top mesh preview if it exists
      if (topMeshPreviewRef.current) {
        disposeTopMeshPreview();
      }

      // Dispose old solid top mesh (don't recreate here to avoid circular dependency)
      disposeTopMeshSolid();
    }

    return topMeshRef.current;
  }

  function ensureTopMeshPreview() {
    // Don't create top mesh preview if thickness is already applied
    if (isThicknessApplied) return null;

    const isMouldMode = selectedSocketType === 'mould';
    if (!isMouldMode) return null;

    if (topMeshPreviewRef.current && topMeshPreviewRef.current.parent) {
      return topMeshPreviewRef.current;
    }

    if (!scene || !shadedMesh) return null;

    const topMeshGeometry = ensureTopMesh();
    if (!topMeshGeometry) return null;

    // Ensure the solid top exists on the shaded mesh as part of base geometry
    ensureTopMeshSolid();

    // Create preview mesh for top
    const topMaterial = CreateTopMeshPreviewMaterial(0.5);
    const topPreview = new THREE.Mesh(topMeshGeometry.clone(), topMaterial);
    topPreview.name = 'TopMeshPreview';
    topPreview.position.copy(shadedMesh.position);
    topPreview.quaternion.copy(shadedMesh.quaternion);
    topPreview.scale.copy(shadedMesh.scale);
    scene.add(topPreview);

    topMeshPreviewRef.current = topPreview;
    return topPreview;
  }

  function disposeTopMeshPreview() {
    const topPreview = topMeshPreviewRef.current;
    if (!topPreview) return;

    if (topPreview.parent) topPreview.parent.remove(topPreview);
    topPreview.geometry?.dispose?.();
    topPreview.material?.dispose?.();
    topMeshPreviewRef.current = null;
  }

  function rebuildTopMeshPreview() {
    // Don't rebuild top mesh preview if thickness is already applied
    if (isThicknessApplied) return;

    const isMouldMode = selectedSocketType === 'mould';
    if (!isMouldMode) {
      disposeTopMeshPreview();
      disposeTopLidPreview(); // Also dispose lid preview
      // Also remove solid top if leaving mould mode
      disposeTopMeshSolid();
      disposeTopLidSolid(); // Also dispose lid solid
      return;
    }

    // Ensure top mesh is generated before creating preview
    ensureTopMesh();

    const topPreview = ensureTopMeshPreview();
    if (!topPreview || !shadedMesh || !topMeshRef.current) return;

    // Get latest thickness state
    const latestThicknessState = useThicknessStore.getState().thickness;
    const topMmValue = parseFloat(latestThicknessState?.topValue ?? 0) || 0;
    const topDeltaUnits = Math.max(0, topMmValue) * MM_TO_UNIT;

    // Get offset value (note: offset is typically for bottom, but can be applied to top too if needed)
    const offsetMmValue = parseFloat(latestThicknessState?.offset ?? 0) || 0;
    const offsetUnits = Math.max(0, offsetMmValue) * MM_TO_UNIT;

    if (topDeltaUnits === 0) {
      // No top thickness => hide preview
      topPreview.visible = false;
      return;
    }

    // Optimize: Only rebuild if thickness actually changed
    const currentThickness = topPreview.userData?.lastThickness;
    if (currentThickness === topDeltaUnits) {
      const previewVisible = useThicknessStore.getState().thickness.previewVisible;
      topPreview.visible = !!previewVisible;
      return;
    }

    // Apply thickness to top mesh - create shell without moving base geometry upward
    const originalTopGeometry = topMeshRef.current.clone();
    originalTopGeometry.computeVertexNormals();
    const topNormals = originalTopGeometry.attributes.normal ? originalTopGeometry.attributes.normal.array : null;

    const topShellGeometry = createTopMeshShell(
      originalTopGeometry,
      originalTopGeometry.attributes.position.array,
      topNormals,
      topDeltaUnits,
      offsetUnits
    );

    if (topShellGeometry) {
      // Add vertex colors to top shell geometry if base geometry has them
      if (baseGeomRef.current?.attributes?.color) {
        const baseColors = baseGeomRef.current.attributes.color.array;
        let r = 0, g = 0, b = 0;
        const count = baseColors.length / 3;
        for (let i = 0; i < count; i++) {
          r += baseColors[i * 3];
          g += baseColors[i * 3 + 1];
          b += baseColors[i * 3 + 2];
        }
        r /= count; g /= count; b /= count;

        // Create color array for shell geometry (outer + inner surfaces)
        const shellVertexCount = topShellGeometry.attributes.position.count;
        const shellColors = new Float32Array(shellVertexCount * 3);
        for (let i = 0; i < shellVertexCount; i++) {
          shellColors[i * 3] = r;
          shellColors[i * 3 + 1] = g;
          shellColors[i * 3 + 2] = b;
        }
        topShellGeometry.setAttribute('color', new THREE.BufferAttribute(shellColors, 3));
      }

      // Update preview with blue transparent material (outer shell preview)
      topPreview.geometry.dispose();
      topPreview.geometry = topShellGeometry;
      topPreview.userData = topPreview.userData || {};
      topPreview.userData.lastThickness = topDeltaUnits;

      const previewVisible = useThicknessStore.getState().thickness.previewVisible;
      topPreview.visible = !!previewVisible;
    }

    // Also rebuild top lid preview
    rebuildTopLidPreview();
  }


  // Create a solid top mesh (no preview material) as a child of shadedMesh
  function ensureTopMeshSolid() {
    // Don't create top mesh solid if thickness is already applied
    if (isThicknessApplied) return null;

    const isMouldMode = selectedSocketType === 'mould';
    if (!isMouldMode) return null;
    if (!scene || !shadedMesh) return null;

    // If already present and attached, keep it
    if (topMeshSolidRef.current && topMeshSolidRef.current.parent === shadedMesh) {
      return topMeshSolidRef.current;
    }

    // Use cached top mesh geometry instead of calling ensureTopMesh() to avoid circular dependency
    let topGeometry = topMeshRef.current;
    if (!topGeometry) {
      // Only generate if not already cached
      topGeometry = ensureTopMesh();
      if (!topGeometry) return null;
    }

    // Clone geometry for safety
    const geom = topGeometry.clone();

    // If base geometry had vertex colors and material uses them, fill with averaged color
    if (baseGeomRef.current?.attributes?.color) {
      const baseColors = baseGeomRef.current.attributes.color.array;
      let r = 0, g = 0, b = 0;
      const count = baseColors.length / 3;
      for (let i = 0; i < count; i++) {
        r += baseColors[i * 3];
        g += baseColors[i * 3 + 1];
        b += baseColors[i * 3 + 2];
      }
      r /= count; g /= count; b /= count;
      const needed = geom.attributes.position.count;
      const colors = new Float32Array(needed * 3);
      for (let i = 0; i < needed; i++) {
        colors[i * 3] = r;
        colors[i * 3 + 1] = g;
        colors[i * 3 + 2] = b;
      }
      geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    }

    // Use original shaded material (same look & vertex colors settings)
    const solidTopMaterial = originalMaterialRef.current || material;
    const solidTop = new THREE.Mesh(geom, solidTopMaterial);
    solidTop.name = 'TopMeshSolid';

    // As a child, inherit shadedMesh transform; keep local transform identity
    solidTop.position.set(0, 0, 0);
    solidTop.quaternion.set(0, 0, 0, 1);
    solidTop.scale.set(1, 1, 1);

    shadedMesh.add(solidTop);
    topMeshSolidRef.current = solidTop;
    return solidTop;
  }

  function disposeTopMeshSolid() {
    const solid = topMeshSolidRef.current;
    if (!solid) return;
    if (solid.parent) solid.parent.remove(solid);
    solid.geometry?.dispose?.();
    // Do NOT dispose material; it is shared with shadedMesh
    topMeshSolidRef.current = null;
  }

  // ==================== TOP LID FUNCTIONS ====================
  
  /**
   * Returns true if point `pt` lies inside the 2D polygon `poly`.
   * Used for triangulation of the lid surface.
   */
  function pointInPolygon(pt, poly) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x, yi = poly[i].y;
      const xj = poly[j].x, yj = poly[j].y;
      const intersect =
        ((yi > pt.y) !== (yj > pt.y)) &&
        (pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  /**
   * Extract the top boundary loop from the thickened upper mesh.
   * This represents the outer boundary after thickness has been applied.
   */
  function getTopBoundaryFromThickenedMesh(thickenedTopGeometry) {
    if (!thickenedTopGeometry || !thickenedTopGeometry.attributes.position) {
      return [];
    }

    try {
      // Find boundary vertices of the thickened mesh
      const boundaryVertices = findTopBoundaryVertices(thickenedTopGeometry);
      
      if (boundaryVertices.length === 0) {
        return [];
      }

      // Get the complete boundary loop starting from the first boundary vertex
      const indices = thickenedTopGeometry.index ? thickenedTopGeometry.index.array : null;
      const boundaryLoop = getBoundaryLoop(thickenedTopGeometry, boundaryVertices[0], indices);

      return boundaryLoop;
    } catch (error) {
      console.warn('Error extracting top boundary from thickened mesh:', error);
      return [];
    }
  }

  /**
   * Create a flat lid surface that covers the top boundary loop using Delaunator triangulation.
   * Similar to holeFilling.js but creates a covering surface instead of filling a hole.
   */
  function createTopLidSurface(topBoundaryLoop, thickenedTopGeometry) {
    if (!topBoundaryLoop || topBoundaryLoop.length < 3 || !thickenedTopGeometry) {
      return null;
    }

    try {
      const positions = thickenedTopGeometry.attributes.position.array;
      
      // Step 1: Extract 3D boundary positions
      const loop3D = topBoundaryLoop.map(i => 
        new THREE.Vector3(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2])
      );

      // Step 2: Calculate centroid and normal for the lid plane
      const centroid = new THREE.Vector3();
      loop3D.forEach(p => centroid.add(p));
      centroid.divideScalar(loop3D.length);

      // Use upward normal for the lid (Y+ direction)
      const normal = new THREE.Vector3(0, 1, 0);

      // Step 3: Create 2D coordinate system on the lid plane
      const tmp = Math.abs(normal.x) > 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);
      const u = new THREE.Vector3().crossVectors(tmp, normal).normalize();
      const v = new THREE.Vector3().crossVectors(normal, u).normalize();

      // Step 4: Project boundary vertices to 2D for triangulation
      const loop2D = loop3D.map(p => {
        const d = p.clone().sub(centroid);
        return new THREE.Vector2(d.dot(u), d.dot(v));
      });

      // Step 5: Create enriched boundary with subdivision for better triangulation
      const maxEdgeLength = 1.0;
      const gridSpacing = 0.5;
      const enriched = [];
      
      for (let i = 0; i < loop3D.length; i++) {
        const A3 = loop3D[i];
        const B3 = loop3D[(i + 1) % loop3D.length];
        const A2 = loop2D[i];
        const B2 = loop2D[(i + 1) % loop2D.length];

        // Add the current vertex
        enriched.push({ p2: A2, p3: A3 });

        // Subdivide edge if it's too long
        const dist = A2.distanceTo(B2);
        const steps = Math.ceil(dist / maxEdgeLength);
        for (let k = 1; k < steps; k++) {
          const t = k / steps;
          const newP2 = new THREE.Vector2(
            A2.x * (1 - t) + B2.x * t,
            A2.y * (1 - t) + B2.y * t
          );
          const newP3 = new THREE.Vector3(
            A3.x * (1 - t) + B3.x * t,
            A3.y * (1 - t) + B3.y * t,
            A3.z * (1 - t) + B3.z * t
          );
          enriched.push({ p2: newP2, p3: newP3 });
        }
      }

      // Step 6: Add interior grid points for better triangulation
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      loop2D.forEach(p => {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
      });

      for (let x = minX; x <= maxX; x += gridSpacing) {
        for (let y = minY; y <= maxY; y += gridSpacing) {
          const pt2 = new THREE.Vector2(x, y);
          if (pointInPolygon(pt2, loop2D)) {
            const p3 = centroid.clone()
              .add(u.clone().multiplyScalar(pt2.x))
              .add(v.clone().multiplyScalar(pt2.y));
            enriched.push({ p2: pt2, p3 });
          }
        }
      }

      // Step 7: Triangulate using Delaunator
      const pts2 = enriched.map(e => [e.p2.x, e.p2.y]);
      const delaunay = Delaunator.from(pts2);
      const tris = delaunay.triangles;

      // Step 8: Filter triangles that are inside the boundary and build final geometry
      const finalPositions = [];
      const finalIndices = [];

      // Add all vertices
      enriched.forEach(e => {
        finalPositions.push(e.p3.x, e.p3.y, e.p3.z);
      });

      // Add triangles that are inside the boundary
      for (let i = 0; i < tris.length; i += 3) {
        const i0 = tris[i], i1 = tris[i + 1], i2 = tris[i + 2];
        const cx = (pts2[i0][0] + pts2[i1][0] + pts2[i2][0]) / 3;
        const cy = (pts2[i0][1] + pts2[i1][1] + pts2[i2][1]) / 3;

        if (pointInPolygon(new THREE.Vector2(cx, cy), loop2D)) {
          // Check face normal orientation and add triangle
          const vA = enriched[i0].p3, vB = enriched[i1].p3, vC = enriched[i2].p3;
          const faceNormal = new THREE.Vector3()
            .subVectors(vC, vA)
            .cross(new THREE.Vector3().subVectors(vB, vA))
            .normalize();

          if (faceNormal.dot(normal) < 0) {
            finalIndices.push(i0, i2, i1); // Flip for correct orientation
          } else {
            finalIndices.push(i0, i1, i2);
          }
        }
      }

      // Create the lid surface geometry
      const lidGeometry = new THREE.BufferGeometry();
      lidGeometry.setAttribute('position', new THREE.Float32BufferAttribute(finalPositions, 3));
      lidGeometry.setIndex(finalIndices);
      lidGeometry.computeVertexNormals();
      lidGeometry.computeBoundingSphere();

      return lidGeometry;

    } catch (error) {
      console.warn('Error creating top lid surface:', error);
      return null;
    }
  }

  /**
   * Apply upward thickness to the lid geometry to create a solid lid with proper normals.
   * Creates a shell with bottom surface, top surface, and side walls.
   */
  function createLidWithThickness(lidSurface, thickness) {
    if (!lidSurface || !lidSurface.attributes.position || thickness <= 0) {
      return null;
    }

    try {
      const originalPositions = lidSurface.attributes.position.array;
      const originalIndices = lidSurface.index ? lidSurface.index.array : null;
      const originalColors = lidSurface.attributes.color ? lidSurface.attributes.color.array : null;
      const vertexCount = originalPositions.length / 3;

      if (!originalIndices) {
        console.warn('Lid surface must be indexed for thickness application');
        return null;
      }

      // Discover boundary edges/vertices (outer ring of the lid)
      const boundaryEdges = findBoundaryEdges(originalIndices);
      const boundaryVertices = new Set();
      boundaryEdges.forEach(([a, b]) => { boundaryVertices.add(a); boundaryVertices.add(b); });

      // Accumulators
      const shellPositions = [];
      const shellIndices = [];

      // 1) Caps (bottom + top)
      for (let i = 0; i < vertexCount; i++) {
        const i3 = i * 3;
        // bottom
        shellPositions.push(
          originalPositions[i3],
          originalPositions[i3 + 1],
          originalPositions[i3 + 2]
        );
      }
      for (let i = 0; i < vertexCount; i++) {
        const i3 = i * 3;
        // top (offset up)
        shellPositions.push(
          originalPositions[i3],
          originalPositions[i3 + 1] + thickness,
          originalPositions[i3 + 2]
        );
      }

      const bottomOffset = 0;
      const topOffset = vertexCount;

      // 2) Dedicated side-wall vertices (duplicate boundary to avoid shared normals)
      const sideBottomMap = new Map();
      const sideTopMap = new Map();
      let runningIndex = vertexCount * 2; // start after caps

      boundaryVertices.forEach((i) => {
        const i3 = i * 3;
        // bottom duplicate
        shellPositions.push(
          originalPositions[i3],
          originalPositions[i3 + 1],
          originalPositions[i3 + 2]
        );
        sideBottomMap.set(i, runningIndex++);
        // top duplicate
        shellPositions.push(
          originalPositions[i3],
          originalPositions[i3 + 1] + thickness,
          originalPositions[i3 + 2]
        );
        sideTopMap.set(i, runningIndex++);
      });

      // 3) Faces for caps
      for (let i = 0; i < originalIndices.length; i += 3) {
        shellIndices.push(
          bottomOffset + originalIndices[i],
          bottomOffset + originalIndices[i + 1],
          bottomOffset + originalIndices[i + 2]
        );
      }
      for (let i = 0; i < originalIndices.length; i += 3) {
        const a = topOffset + originalIndices[i];
        const b = topOffset + originalIndices[i + 1];
        const c = topOffset + originalIndices[i + 2];
        shellIndices.push(c, b, a); // flipped for outward normals
      }

      // 4) Faces for side walls using side-specific vertices
      boundaryEdges.forEach(([a, b]) => {
        const sbA = sideBottomMap.get(a);
        const sbB = sideBottomMap.get(b);
        const stA = sideTopMap.get(a);
        const stB = sideTopMap.get(b);
        // Quad triangles
        shellIndices.push(sbA, stA, stB);
        shellIndices.push(sbA, stB, sbB);
      });

      // Build geometry
      const lidWithThickness = new THREE.BufferGeometry();
      lidWithThickness.setAttribute('position', new THREE.Float32BufferAttribute(shellPositions, 3));
      lidWithThickness.setIndex(shellIndices);

      // Colors (if present on the lid surface)
      if (originalColors) {
        const totalVertexCount = shellPositions.length / 3;
        const shellColors = new Float32Array(totalVertexCount * 3);

        // bottom colors
        shellColors.set(originalColors, 0);
        // top colors (copy from bottom)
        shellColors.set(originalColors, originalColors.length);

        // side duplicates copy boundary colors
        let vPtr = vertexCount * 2; // start vertex index for side duplicates
        boundaryVertices.forEach((i) => {
          const c3 = i * 3;
          // bottom duplicate
          let off = vPtr * 3;
          shellColors[off] = originalColors[c3];
          shellColors[off + 1] = originalColors[c3 + 1];
          shellColors[off + 2] = originalColors[c3 + 2];
          vPtr++;
          // top duplicate
          off = vPtr * 3;
          shellColors[off] = originalColors[c3];
          shellColors[off + 1] = originalColors[c3 + 1];
          shellColors[off + 2] = originalColors[c3 + 2];
          vPtr++;
        });

        lidWithThickness.setAttribute('color', new THREE.BufferAttribute(shellColors, 3));
      }

      lidWithThickness.computeVertexNormals();
      lidWithThickness.computeBoundingSphere();

      return lidWithThickness;

    } catch (error) {
      console.warn('Error creating lid with thickness:', error);
      return null;
    }
  }

  /**
   * Generate the complete top lid from the thickened upper mesh
   */
  function generateTopLid(thickenedTopGeometry, thickness) {
    if (!thickenedTopGeometry || thickness <= 0) {
      return null;
    }

    try {
      // Step 1: Extract top boundary from thickened mesh
      const topBoundary = getTopBoundaryFromThickenedMesh(thickenedTopGeometry);
      if (topBoundary.length === 0) {
        return null;
      }

      // Step 2: Create flat lid surface
      const lidSurface = createTopLidSurface(topBoundary, thickenedTopGeometry);
      if (!lidSurface) {
        return null;
      }

      // Step 3: Apply upward thickness to create solid lid
      const lidWithThickness = createLidWithThickness(lidSurface, thickness);
      
      // Dispose intermediate geometry
      lidSurface.dispose();
      
      return lidWithThickness;

    } catch (error) {
      console.warn('Error generating top lid:', error);
      return null;
    }
  }

  /**
   * Ensure top lid preview exists and is properly managed
   */
  function ensureTopLidPreview() {
    // Don't create lid preview if thickness is already applied
    if (isThicknessApplied) return null;

    const isMouldMode = selectedSocketType === 'mould';
    if (!isMouldMode) return null;

    if (topLidPreviewRef.current && topLidPreviewRef.current.parent) {
      return topLidPreviewRef.current;
    }

    if (!scene || !shadedMesh) return null;

    // Get the thickened top mesh geometry to generate lid from
    if (!topMeshRef.current) return null;

    // Get current thickness
    const latestThicknessState = useThicknessStore.getState().thickness;
    const topMmValue = parseFloat(latestThicknessState?.topValue ?? 0) || 0;
    const topDeltaUnits = Math.max(0, topMmValue) * MM_TO_UNIT;

    // Get offset value
    const offsetMmValue = parseFloat(latestThicknessState?.offset ?? 0) || 0;
    const offsetUnits = Math.max(0, offsetMmValue) * MM_TO_UNIT;

    if (topDeltaUnits === 0) return null;

    // First we need the thickened top mesh geometry
    const originalTopGeometry = topMeshRef.current.clone();
    originalTopGeometry.computeVertexNormals();
    const topNormals = originalTopGeometry.attributes.normal ? originalTopGeometry.attributes.normal.array : null;

    const topShellGeometry = createTopMeshShell(
      originalTopGeometry,
      originalTopGeometry.attributes.position.array,
      topNormals,
      topDeltaUnits,
      offsetUnits
    );

    if (!topShellGeometry) return null;

    // Generate the lid
    const lidGeometry = generateTopLid(topShellGeometry, topDeltaUnits);
    if (!lidGeometry) {
      topShellGeometry.dispose();
      return null;
    }

    // Create preview mesh for lid
    const lidMaterial = CreateTopMeshPreviewMaterial(0.3); // Slightly more transparent than top mesh
    const lidPreview = new THREE.Mesh(lidGeometry, lidMaterial);
    lidPreview.name = 'TopLidPreview';
    lidPreview.position.copy(shadedMesh.position);
    lidPreview.quaternion.copy(shadedMesh.quaternion);
    lidPreview.scale.copy(shadedMesh.scale);
    scene.add(lidPreview);

    topLidPreviewRef.current = lidPreview;
    topLidRef.current = lidGeometry.clone(); // Cache the lid geometry

    // Dispose temporary geometry
    topShellGeometry.dispose();
    originalTopGeometry.dispose();

    return lidPreview;
  }

  /**
   * Dispose top lid preview
   */
  function disposeTopLidPreview() {
    const lidPreview = topLidPreviewRef.current;
    if (!lidPreview) return;

    if (lidPreview.parent) lidPreview.parent.remove(lidPreview);
    lidPreview.geometry?.dispose?.();
    lidPreview.material?.dispose?.();
    topLidPreviewRef.current = null;
  }

  /**
   * Rebuild the top lid preview when thickness changes
   */
  function rebuildTopLidPreview() {
    // Don't rebuild lid preview if thickness is already applied
    if (isThicknessApplied) return;

    const isMouldMode = selectedSocketType === 'mould';
    if (!isMouldMode) {
      disposeTopLidPreview();
      return;
    }

    // Dispose old preview before creating new one
    disposeTopLidPreview();

    const lidPreview = ensureTopLidPreview();
    if (!lidPreview || !shadedMesh) return;

    // Get latest thickness state
    const latestThicknessState = useThicknessStore.getState().thickness;
    const topMmValue = parseFloat(latestThicknessState?.topValue ?? 0) || 0;
    const previewVisible = !!latestThicknessState?.previewVisible;
    
    // Show/hide based on thickness value and preview visibility
    lidPreview.visible = previewVisible && topMmValue > 0;
  }

  /**
   * Create solid top lid attached to shadedMesh
   */
  function ensureTopLidSolid() {
    // Don't create lid solid if thickness is already applied
    if (isThicknessApplied) return null;

    const isMouldMode = selectedSocketType === 'mould';
    if (!isMouldMode) return null;
    if (!scene || !shadedMesh) return null;

    // If already present and attached, keep it
    if (topLidSolidRef.current && topLidSolidRef.current.parent === shadedMesh) {
      return topLidSolidRef.current;
    }

    // Use cached lid geometry
    let lidGeometry = topLidRef.current;
    if (!lidGeometry) {
      // Generate lid if not cached
      const latestThicknessState = useThicknessStore.getState().thickness;
      const topMmValue = parseFloat(latestThicknessState?.topValue ?? 0) || 0;
      const topDeltaUnits = Math.max(0, topMmValue) * MM_TO_UNIT;

      // Get offset value
      const offsetMmValue = parseFloat(latestThicknessState?.offset ?? 0) || 0;
      const offsetUnits = Math.max(0, offsetMmValue) * MM_TO_UNIT;

      if (topDeltaUnits === 0 || !topMeshRef.current) return null;

      const originalTopGeometry = topMeshRef.current.clone();
      originalTopGeometry.computeVertexNormals();
      const topNormals = originalTopGeometry.attributes.normal ? originalTopGeometry.attributes.normal.array : null;

      const topShellGeometry = createTopMeshShell(
        originalTopGeometry,
        originalTopGeometry.attributes.position.array,
        topNormals,
        topDeltaUnits,
        offsetUnits
      );

      if (!topShellGeometry) {
        originalTopGeometry.dispose();
        return null;
      }

      lidGeometry = generateTopLid(topShellGeometry, topDeltaUnits);
      
      // Clean up
      topShellGeometry.dispose();
      originalTopGeometry.dispose();
      
      if (!lidGeometry) return null;
      
      topLidRef.current = lidGeometry;
    }

    // Clone geometry for safety
    const geom = lidGeometry.clone();

    // Handle vertex colors if needed (similar to ensureTopMeshSolid)
    if (baseGeomRef.current?.attributes?.color) {
      const baseColors = baseGeomRef.current.attributes.color.array;
      let r = 0, g = 0, b = 0;
      const count = baseColors.length / 3;
      for (let i = 0; i < count; i++) {
        r += baseColors[i * 3];
        g += baseColors[i * 3 + 1];
        b += baseColors[i * 3 + 2];
      }
      r /= count; g /= count; b /= count;
      
      const needed = geom.attributes.position.count;
      const colors = new Float32Array(needed * 3);
      for (let i = 0; i < needed; i++) {
        colors[i * 3] = r;
        colors[i * 3 + 1] = g;
        colors[i * 3 + 2] = b;
      }
      geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    }

    // Use original shaded material
    const solidLidMaterial = originalMaterialRef.current || material;
    const solidLid = new THREE.Mesh(geom, solidLidMaterial);
    solidLid.name = 'TopLidSolid';

    // As a child, inherit shadedMesh transform
    solidLid.position.set(0, 0, 0);
    solidLid.quaternion.set(0, 0, 0, 1);
    solidLid.scale.set(1, 1, 1);

    shadedMesh.add(solidLid);
    topLidSolidRef.current = solidLid;
    return solidLid;
  }

  /**
   * Dispose solid top lid
   */
  function disposeTopLidSolid() {
    const solid = topLidSolidRef.current;
    if (!solid) return;
    if (solid.parent) solid.parent.remove(solid);
    solid.geometry?.dispose?.();
    // Do NOT dispose material; it is shared with shadedMesh
    topLidSolidRef.current = null;
  }

  function rebuildPreview() {
    // Don't rebuild preview if thickness is already applied
    if (isThicknessApplied) return;

    const p = ensurePreview();
    if (!p || !shadedMesh) return;

    // Get latest state directly from store to avoid stale closure issues
    const latestThicknessState = useThicknessStore.getState().thickness;

    // User-provided thickness in mm -> scene units (cm)
    const mmValue = parseFloat(latestThicknessState?.value ?? 0) || 0;
    const deltaUnits = Math.max(0, mmValue) * MM_TO_UNIT;

    // User-provided offset in mm -> scene units (cm)
    const offsetMmValue = parseFloat(latestThicknessState?.offset ?? 0) || 0;
    const offsetUnits = Math.max(0, offsetMmValue) * MM_TO_UNIT;

    if (deltaUnits === 0) {
      // No bottom thickness => hide preview overlay
      p.visible = false;
    } else {
      // Create solid shell with thickness and offset
      const shellGeometry = createSolidShell(
        baseGeomRef.current,
        basePosRef.current,
        baseNormalsRef.current,
        deltaUnits,
        offsetUnits
      );

      if (shellGeometry) {
        // Dispose old geometry
        p.geometry.dispose();

        // Replace with new shell geometry
        p.geometry = shellGeometry;

        const previewVisible = useThicknessStore.getState().thickness.previewVisible;
        p.visible = !!previewVisible;

        // Color handling is already done in createSolidShell for the new vertex duplication structure
      }
    }

    // Top mesh preview is rebuilt by its own effect when top thickness changes
    // Also rebuild top lid preview
    rebuildTopLidPreview();
  }

  /**
   * Compute a per-vertex maximum safe thickness along the provided normals by
   * raycasting into the base geometry using a BVH. For each vertex we find
   * the first intersection along its normal direction (beyond a small epsilon)
   * and treat that distance as an upper bound for the shell thickness at that
   * vertex. If no intersection is found the thickness is considered unbounded
   * (Infinity) and will be clamped later by the user-selected global thickness.
   *
   * This runs once per base geometry snapshot and is reused for subsequent
   * preview rebuilds to keep the interactive slider responsive.
   */
  function computePerVertexMaxThickness(baseGeometry, basePositions, baseNormals) {
    if (!baseGeometry || !basePositions || !baseNormals) return null;

    const vertexCount = basePositions.length / 3;
    if (!vertexCount) return null;

    // Ensure bounding data exists
    if (!baseGeometry.boundingSphere) {
      baseGeometry.computeBoundingSphere();
    }
    const radius = baseGeometry.boundingSphere?.radius || 1;

    // Small offset to skip self-hits and a conservative far distance
    const epsilon = Math.max(radius * 1e-4, 1e-4);
    const maxRayDistance = radius * 4 || Infinity;

    try {
      if (!baseGeometry.boundsTree) {
        // Build BVH once; dispose is handled when geometry snapshot is discarded
        computeBoundsTree.call(baseGeometry);
      }
    } catch (error) {
      console.warn('[ThicknessHandler] Failed to build BVH for adaptive thickness:', error);
      return null;
    }

    const raycaster = new THREE.Raycaster();
    raycaster.firstHitOnly = true; // Honored by three-mesh-bvh; harmless otherwise
    raycaster.near = epsilon;
    raycaster.far = maxRayDistance;
    raycaster.params.Mesh = raycaster.params.Mesh || {};
    raycaster.params.Mesh.side = THREE.FrontSide;

    // Use a lightweight helper mesh that never enters the scene graph
    const helperMaterial = new THREE.MeshBasicMaterial();
    const helperMesh = new THREE.Mesh(baseGeometry, helperMaterial);
    helperMesh.matrixAutoUpdate = false;
    helperMesh.matrixWorld.identity();

    const maxField = new Float32Array(vertexCount);
    const origin = new THREE.Vector3();
    const normal = new THREE.Vector3();

    for (let i = 0; i < vertexCount; i++) {
      const i3 = i * 3;

      origin.set(
        basePositions[i3],
        basePositions[i3 + 1],
        basePositions[i3 + 2]
      );

      normal.set(
        baseNormals[i3],
        baseNormals[i3 + 1],
        baseNormals[i3 + 2]
      );

      const nLenSq = normal.lengthSq();
      if (!Number.isFinite(nLenSq) || nLenSq < 1e-12) {
        // Degenerate normal – safest is to avoid outward offset here
        maxField[i] = 0;
        continue;
      }
      normal.normalize();

      raycaster.ray.origin.copy(origin);
      raycaster.ray.direction.copy(normal);

      const hits = raycaster.intersectObject(helperMesh, false);
      const firstHit = hits && hits.length > 0 ? hits[0] : null;

      if (firstHit && Number.isFinite(firstHit.distance) && firstHit.distance > epsilon) {
        // Leave a small safety margin so the shell does not touch the hit surface
        const safetyMargin = epsilon * 4;
        const safe = Math.max(0, firstHit.distance - safetyMargin);
        maxField[i] = safe;
      } else {
        // No obstruction along the normal: treat as unbounded and clamp later
        maxField[i] = Infinity;
      }
    }

    helperMaterial.dispose();
    // Keep baseGeometry.boundsTree for potential re-use while this snapshot lives

    return maxField;
  }

  function createTopMeshShell(topGeometry, originalPositions, originalNormals, thickness, offset = 0) {
    if (!topGeometry || !originalPositions || !originalNormals || thickness <= 0) return null;

    const topPositions = topGeometry.attributes.position.array;
    const indices = topGeometry.index ? topGeometry.index.array : null;
    const vertexCount = topPositions.length / 3;

    // Find boundary edges first to determine which vertices need duplication
    const boundaryEdges = !topGeometry.userData?.isClosed ?
      findBoundaryEdges(indices || generateIndicesForNonIndexed(vertexCount)) : [];

    // Create set of boundary vertex indices
    const boundaryVertices = new Set();
    boundaryEdges.forEach(([a, b]) => {
      boundaryVertices.add(a);
      boundaryVertices.add(b);
    });

    // Step 1: Apply offset to top positions to create comfort space
    const offsetInnerPositions = applyOffsetToPositions(topPositions, originalNormals, offset);

    // Pre-compute displaced outer positions and smooth them BEFORE creating shell
    const outerDisplacedPositions = new Float32Array(vertexCount * 3);
    for (let i = 0; i < vertexCount; i++) {
      const i3 = i * 3;
      const x = offsetInnerPositions[i3];
      const y = offsetInnerPositions[i3 + 1];
      const z = offsetInnerPositions[i3 + 2];
      const nx = originalNormals[i3];
      const ny = originalNormals[i3 + 1];
      const nz = originalNormals[i3 + 2];

      outerDisplacedPositions[i3] = x + nx * thickness;
      outerDisplacedPositions[i3 + 1] = y + ny * thickness;
      outerDisplacedPositions[i3 + 2] = z + nz * thickness;
    }

    // Apply rim boundary smoothing to displaced positions BEFORE shell creation
    const thicknessStateForRim2 = useThicknessStore.getState().thickness;
    if (thicknessStateForRim2?.rimBoundarySmoothing?.enabled && boundaryEdges.length > 0) {
      try {
        // Use absolute minimum thickness in scene units, clamped to not exceed main thickness
        const minimumThicknessMm = thicknessStateForRim2.rimBoundarySmoothing.minimumThickness || 1.0;
        const minimumThicknessForRim = Math.max(0, Math.min(thickness, minimumThicknessMm * MM_TO_UNIT));
        applyRimBoundarySmoothing(
          offsetInnerPositions,
          outerDisplacedPositions,
          vertexCount,
          boundaryVertices,
          boundaryEdges,
          thicknessStateForRim2.rimBoundarySmoothing.smoothnessLength,
          thicknessStateForRim2.rimBoundarySmoothing.easingProfile,
          minimumThicknessForRim
        );
        console.log('[ThicknessHandler] Applied rim boundary smoothing to top mesh outer positions');
      } catch (error) {
        console.warn('[ThicknessHandler] Failed to apply rim boundary smoothing to top mesh:', error);
      }
    }

    // Apply smoothing to BOTH inner and outer boundary surfaces for consistent rim smoothness
    if (boundaryEdges.length > 0) {
      try {
        const smoothedOuter = smoothBoundaryVertices(outerDisplacedPositions, indices || generateIndicesForNonIndexed(vertexCount), 5, 0.6);
        outerDisplacedPositions.set(smoothedOuter);
        console.log('[ThicknessHandler] Applied boundary smoothing to top mesh outer surface');

        // Also smooth the inner boundary to match outer smoothness
        const smoothedInner = smoothBoundaryVertices(offsetInnerPositions, indices || generateIndicesForNonIndexed(vertexCount), 5, 0.6);
        offsetInnerPositions.set(smoothedInner);
        console.log('[ThicknessHandler] Applied boundary smoothing to top mesh inner surface');
      } catch (error) {
        console.warn('[ThicknessHandler] Failed to apply boundary smoothing to top mesh:', error);
      }
    }

    // Create arrays for shell geometry with duplicated boundary vertices
    const shellPositions = [];
    const shellIndices = [];

    // Vertex index mapping for the new geometry
    let currentVertexIndex = 0;
    const outerVertexMap = new Map(); // original index -> new outer vertex index
    const innerVertexMap = new Map(); // original index -> new inner vertex index
    const outerBoundaryMap = new Map(); // original index -> outer boundary vertex index
    const innerBoundaryMap = new Map(); // original index -> inner boundary vertex index

    // 1. Create outer surface vertices (non-boundary) using pre-smoothed positions
    for (let i = 0; i < vertexCount; i++) {
      if (!boundaryVertices.has(i)) {
        const i3 = i * 3;
        // Use pre-smoothed displaced positions
        shellPositions.push(outerDisplacedPositions[i3], outerDisplacedPositions[i3 + 1], outerDisplacedPositions[i3 + 2]);
        outerVertexMap.set(i, currentVertexIndex++);
      }
    }

    // 2. Create outer boundary vertices (for outer surface faces)
    for (let i = 0; i < vertexCount; i++) {
      if (boundaryVertices.has(i)) {
        const i3 = i * 3;
        // Use pre-smoothed displaced positions
        shellPositions.push(outerDisplacedPositions[i3], outerDisplacedPositions[i3 + 1], outerDisplacedPositions[i3 + 2]);
        const boundaryVertexIdx = currentVertexIndex++;
        outerVertexMap.set(i, boundaryVertexIdx);
        outerBoundaryMap.set(i, boundaryVertexIdx);
      }
    }

    // 3. Create inner surface vertices (non-boundary) - using offset positions
    for (let i = 0; i < vertexCount; i++) {
      if (!boundaryVertices.has(i)) {
        const i3 = i * 3;
        shellPositions.push(offsetInnerPositions[i3], offsetInnerPositions[i3 + 1], offsetInnerPositions[i3 + 2]);
        innerVertexMap.set(i, currentVertexIndex++);
      }
    }

    // 4. Create inner boundary vertices (for inner surface faces) - using offset positions
    for (let i = 0; i < vertexCount; i++) {
      if (boundaryVertices.has(i)) {
        const i3 = i * 3;
        shellPositions.push(offsetInnerPositions[i3], offsetInnerPositions[i3 + 1], offsetInnerPositions[i3 + 2]);
        const boundaryVertexIdx = currentVertexIndex++;
        innerVertexMap.set(i, boundaryVertexIdx);
        innerBoundaryMap.set(i, boundaryVertexIdx);
      }
    }

    // 5. Create DEDICATED side wall boundary vertices (separate copies for proper normals)
    const sideWallOuterMap = new Map(); // original index -> side wall outer vertex index
    const sideWallInnerMap = new Map(); // original index -> side wall inner vertex index
    for (let i = 0; i < vertexCount; i++) {
      if (boundaryVertices.has(i)) {
        const i3 = i * 3;

        // Side wall outer vertex (duplicate of outer boundary vertex)
        shellPositions.push(outerDisplacedPositions[i3], outerDisplacedPositions[i3 + 1], outerDisplacedPositions[i3 + 2]);
        sideWallOuterMap.set(i, currentVertexIndex++);

        // Side wall inner vertex (duplicate of inner boundary vertex) - using offset positions
        shellPositions.push(offsetInnerPositions[i3], offsetInnerPositions[i3 + 1], offsetInnerPositions[i3 + 2]);
        sideWallInnerMap.set(i, currentVertexIndex++);
      }
    }

    // 6. Create faces for outer surface
    if (indices) {
      for (let i = 0; i < indices.length; i += 3) {
        const a = outerVertexMap.get(indices[i]);
        const b = outerVertexMap.get(indices[i + 1]);
        const c = outerVertexMap.get(indices[i + 2]);
        shellIndices.push(a, b, c);
      }
    } else {
      for (let i = 0; i < vertexCount; i += 3) {
        const a = outerVertexMap.get(i);
        const b = outerVertexMap.get(i + 1);
        const c = outerVertexMap.get(i + 2);
        shellIndices.push(a, b, c);
      }
    }

    // 7. Create faces for inner surface (flipped winding)
    if (indices) {
      for (let i = 0; i < indices.length; i += 3) {
        const a = innerVertexMap.get(indices[i]);
        const b = innerVertexMap.get(indices[i + 1]);
        const c = innerVertexMap.get(indices[i + 2]);
        shellIndices.push(c, b, a); // Flipped winding
      }
    } else {
      for (let i = 0; i < vertexCount; i += 3) {
        const a = innerVertexMap.get(i);
        const b = innerVertexMap.get(i + 1);
        const c = innerVertexMap.get(i + 2);
        shellIndices.push(c, b, a); // Flipped winding
      }
    }

    // 8. Create side walls using dedicated boundary vertex copies
    if (boundaryEdges.length > 0) {
      createSideWallsWithDuplicatedVertices(shellIndices, boundaryEdges, sideWallOuterMap, sideWallInnerMap);
    }

    // Create the shell geometry
    const shellGeometry = new THREE.BufferGeometry();
    shellGeometry.setAttribute('position', new THREE.Float32BufferAttribute(shellPositions, 3));
    shellGeometry.setIndex(shellIndices);

    // Apply Laplacian smoothing to outer surface if enabled
    // NOTE: Only smooth non-boundary vertices to preserve rim shape
    const thicknessState = useThicknessStore.getState().thickness;
    if (thicknessState?.outerSurfaceSmoothing?.enabled && thicknessState.outerSurfaceSmoothing.iterations > 0) {
      try {
        // Build adjacency for the shell geometry BEFORE smoothing
        const neighbors = buildAdjacency(shellGeometry);
        const isBoundary = detectBoundaryVertices(shellGeometry);

        // Collect outer vertex indices (non-boundary vertices only for smoothing)
        const outerVertexIndices = [];
        for (const [origIdx, newIdx] of outerVertexMap.entries()) {
          // Only add non-boundary vertices - boundary vertices should never be smoothed
          if (!boundaryVertices.has(origIdx)) {
            outerVertexIndices.push(newIdx);
          }
        }
        // Note: We explicitly exclude boundary vertices from the smoothing list
        // This ensures the rim shape is preserved and never affected by smoothing

        if (outerVertexIndices.length > 0) {
          // Apply smoothing to outer vertices (boundary vertices will be skipped internally)
          applySurfaceSmoothing(
            shellGeometry,
            outerVertexIndices,
            neighbors,
            isBoundary,
            thicknessState.outerSurfaceSmoothing.iterations
          );
        }
      } catch (error) {
        console.warn('[ThicknessHandler] Failed to apply outer surface smoothing to top mesh:', error);
      }
    }

    // Handle vertex colors if they exist in the base geometry
    if (topGeometry.attributes.color) {
      const originalColors = topGeometry.attributes.color.array;
      const shellColors = [];

      // Add colors for outer vertices (non-boundary)
      for (let i = 0; i < vertexCount; i++) {
        if (!boundaryVertices.has(i)) {
          const i3 = i * 3;
          shellColors.push(originalColors[i3], originalColors[i3 + 1], originalColors[i3 + 2]);
        }
      }

      // Add colors for outer boundary vertices
      for (let i = 0; i < vertexCount; i++) {
        if (boundaryVertices.has(i)) {
          const i3 = i * 3;
          shellColors.push(originalColors[i3], originalColors[i3 + 1], originalColors[i3 + 2]);
        }
      }

      // Add colors for inner vertices (non-boundary)
      for (let i = 0; i < vertexCount; i++) {
        if (!boundaryVertices.has(i)) {
          const i3 = i * 3;
          shellColors.push(originalColors[i3], originalColors[i3 + 1], originalColors[i3 + 2]);
        }
      }

      // Add colors for inner boundary vertices
      for (let i = 0; i < vertexCount; i++) {
        if (boundaryVertices.has(i)) {
          const i3 = i * 3;
          shellColors.push(originalColors[i3], originalColors[i3 + 1], originalColors[i3 + 2]);
        }
      }

      // Add colors for side wall vertices
      for (let i = 0; i < vertexCount; i++) {
        if (boundaryVertices.has(i)) {
          const i3 = i * 3;
          // Side wall outer vertex
          shellColors.push(originalColors[i3], originalColors[i3 + 1], originalColors[i3 + 2]);
          // Side wall inner vertex
          shellColors.push(originalColors[i3], originalColors[i3 + 1], originalColors[i3 + 2]);
        }
      }

      shellGeometry.setAttribute('color', new THREE.Float32BufferAttribute(shellColors, 3));
    }

    // Compute normals for proper lighting
    shellGeometry.computeVertexNormals();
    shellGeometry.computeBoundingSphere();

    return shellGeometry;
  }

  function createSolidShell(baseGeometry, basePositions, baseNormals, thickness, offset = 0) {
    if (!baseGeometry || !basePositions || !baseNormals || thickness <= 0) return null;

    const positions = baseGeometry.attributes.position.array;
    const indices = baseGeometry.index ? baseGeometry.index.array : null;
    const vertexCount = positions.length / 3;

    // Find boundary edges first to determine which vertices need duplication
    const boundaryEdges = !baseGeometry.userData?.isClosed ?
      findBoundaryEdges(indices || generateIndicesForNonIndexed(vertexCount)) : [];

    // Create set of boundary vertex indices
    const boundaryVertices = new Set();
    boundaryEdges.forEach(([a, b]) => {
      boundaryVertices.add(a);
      boundaryVertices.add(b);
    });

    // Step 1: Apply offset to base positions to create comfort space for wearer
    // This moves the inner surface outward while keeping the outer surface
    // at the correct thickness distance from the offset inner surface
    const offsetInnerPositions = applyOffsetToPositions(basePositions, baseNormals, offset);

    // Pre-compute displaced outer positions and smooth them BEFORE creating shell.
    // Use an adaptive per-vertex thickness field if available to avoid
    // self-intersections in highly concave regions.
    // Total displacement = offset + thickness (from original position)
    const outerDisplacedPositions = new Float32Array(vertexCount * 3);
    for (let i = 0; i < vertexCount; i++) {
      const i3 = i * 3;
      const x = offsetInnerPositions[i3];
      const y = offsetInnerPositions[i3 + 1];
      const z = offsetInnerPositions[i3 + 2];
      const nx = baseNormals[i3];
      const ny = baseNormals[i3 + 1];
      const nz = baseNormals[i3 + 2];
      // Clamp the local thickness by the precomputed safe bound for this vertex.
      const maxForVertex =
        baseMaxThicknessRef.current &&
        baseMaxThicknessRef.current.length === vertexCount
          ? baseMaxThicknessRef.current[i]
          : Infinity;
      const localThickness = Math.max(
        0,
        Math.min(thickness, Number.isFinite(maxForVertex) ? maxForVertex : thickness)
      );

      outerDisplacedPositions[i3] = x + nx * localThickness;
      outerDisplacedPositions[i3 + 1] = y + ny * localThickness;
      outerDisplacedPositions[i3 + 2] = z + nz * localThickness;
    }

    // Apply rim boundary smoothing to displaced positions BEFORE shell creation
    const thicknessStateForRim = useThicknessStore.getState().thickness;
    if (thicknessStateForRim?.rimBoundarySmoothing?.enabled && boundaryEdges.length > 0) {
      try {
        // Use absolute minimum thickness in scene units, clamped to not exceed actual thickness
        const minimumThicknessMm = thicknessStateForRim.rimBoundarySmoothing.minimumThickness || 1.0;
        const minimumThicknessForRim = Math.max(0, Math.min(thickness, minimumThicknessMm * MM_TO_UNIT));
        applyRimBoundarySmoothing(
          offsetInnerPositions,
          outerDisplacedPositions,
          vertexCount,
          boundaryVertices,
          boundaryEdges,
          thicknessStateForRim.rimBoundarySmoothing.smoothnessLength,
          thicknessStateForRim.rimBoundarySmoothing.easingProfile,
          minimumThicknessForRim
        );
        console.log('[ThicknessHandler] Applied rim boundary smoothing to outer displaced positions');
      } catch (error) {
        console.warn('[ThicknessHandler] Failed to apply rim boundary smoothing:', error);
      }
    }

    // Apply smoothing to BOTH inner and outer boundary surfaces for consistent rim smoothness
    if (boundaryEdges.length > 0) {
      try {
        const smoothedOuter = smoothBoundaryVertices(outerDisplacedPositions, indices || generateIndicesForNonIndexed(vertexCount), 5, 0.6);
        outerDisplacedPositions.set(smoothedOuter);
        console.log('[ThicknessHandler] Applied boundary smoothing to outer surface');

        // Also smooth the inner boundary to match outer smoothness
        const smoothedInner = smoothBoundaryVertices(offsetInnerPositions, indices || generateIndicesForNonIndexed(vertexCount), 5, 0.6);
        offsetInnerPositions.set(smoothedInner);
        console.log('[ThicknessHandler] Applied boundary smoothing to inner surface');
      } catch (error) {
        console.warn('[ThicknessHandler] Failed to apply boundary smoothing:', error);
      }
    }

    // Create arrays for shell geometry with duplicated boundary vertices
    const shellPositions = [];
    const shellIndices = [];

    // Vertex index mapping for the new geometry
    let currentVertexIndex = 0;
    const outerVertexMap = new Map(); // original index -> new outer vertex index
    const innerVertexMap = new Map(); // original index -> new inner vertex index
    const outerBoundaryMap = new Map(); // original index -> outer boundary vertex index
    const innerBoundaryMap = new Map(); // original index -> inner boundary vertex index

    // 1. Create outer surface vertices (non-boundary) using pre-smoothed positions
    for (let i = 0; i < vertexCount; i++) {
      if (!boundaryVertices.has(i)) {
        const i3 = i * 3;
        // Use pre-smoothed displaced positions
        shellPositions.push(outerDisplacedPositions[i3], outerDisplacedPositions[i3 + 1], outerDisplacedPositions[i3 + 2]);
        outerVertexMap.set(i, currentVertexIndex++);
      }
    }

    // 2. Create outer boundary vertices (for outer surface faces)
    for (let i = 0; i < vertexCount; i++) {
      if (boundaryVertices.has(i)) {
        const i3 = i * 3;
        // Use pre-smoothed displaced positions
        shellPositions.push(outerDisplacedPositions[i3], outerDisplacedPositions[i3 + 1], outerDisplacedPositions[i3 + 2]);
        const boundaryVertexIdx = currentVertexIndex++;
        outerVertexMap.set(i, boundaryVertexIdx);
        outerBoundaryMap.set(i, boundaryVertexIdx);
      }
    }

    // 3. Create inner surface vertices (non-boundary) - using offset positions
    for (let i = 0; i < vertexCount; i++) {
      if (!boundaryVertices.has(i)) {
        const i3 = i * 3;
        shellPositions.push(offsetInnerPositions[i3], offsetInnerPositions[i3 + 1], offsetInnerPositions[i3 + 2]);
        innerVertexMap.set(i, currentVertexIndex++);
      }
    }

    // 4. Create inner boundary vertices (for inner surface faces) - using offset positions
    for (let i = 0; i < vertexCount; i++) {
      if (boundaryVertices.has(i)) {
        const i3 = i * 3;
        shellPositions.push(offsetInnerPositions[i3], offsetInnerPositions[i3 + 1], offsetInnerPositions[i3 + 2]);
        const boundaryVertexIdx = currentVertexIndex++;
        innerVertexMap.set(i, boundaryVertexIdx);
        innerBoundaryMap.set(i, boundaryVertexIdx);
      }
    }

    // 5. Create DEDICATED side wall boundary vertices (separate copies for proper normals)
    const sideWallOuterMap = new Map(); // original index -> side wall outer vertex index
    const sideWallInnerMap = new Map(); // original index -> side wall inner vertex index
    for (let i = 0; i < vertexCount; i++) {
      if (boundaryVertices.has(i)) {
        const i3 = i * 3;

        // Side wall outer vertex (duplicate of outer boundary vertex)
        shellPositions.push(outerDisplacedPositions[i3], outerDisplacedPositions[i3 + 1], outerDisplacedPositions[i3 + 2]);
        sideWallOuterMap.set(i, currentVertexIndex++);

        // Side wall inner vertex (duplicate of inner boundary vertex) - using offset positions
        shellPositions.push(offsetInnerPositions[i3], offsetInnerPositions[i3 + 1], offsetInnerPositions[i3 + 2]);
        sideWallInnerMap.set(i, currentVertexIndex++);
      }
    }

    // 6. Create faces for outer surface
    if (indices) {
      for (let i = 0; i < indices.length; i += 3) {
        const a = outerVertexMap.get(indices[i]);
        const b = outerVertexMap.get(indices[i + 1]);
        const c = outerVertexMap.get(indices[i + 2]);
        shellIndices.push(a, b, c);
      }
    } else {
      for (let i = 0; i < vertexCount; i += 3) {
        const a = outerVertexMap.get(i);
        const b = outerVertexMap.get(i + 1);
        const c = outerVertexMap.get(i + 2);
        shellIndices.push(a, b, c);
      }
    }

    // 7. Create faces for inner surface (flipped winding)
    if (indices) {
      for (let i = 0; i < indices.length; i += 3) {
        const a = innerVertexMap.get(indices[i]);
        const b = innerVertexMap.get(indices[i + 1]);
        const c = innerVertexMap.get(indices[i + 2]);
        shellIndices.push(c, b, a); // Flipped winding
      }
    } else {
      for (let i = 0; i < vertexCount; i += 3) {
        const a = innerVertexMap.get(i);
        const b = innerVertexMap.get(i + 1);
        const c = innerVertexMap.get(i + 2);
        shellIndices.push(c, b, a); // Flipped winding
      }
    }

    // 8. Create side walls using dedicated boundary vertex copies
    if (boundaryEdges.length > 0) {
      createSideWallsWithDuplicatedVertices(shellIndices, boundaryEdges, sideWallOuterMap, sideWallInnerMap);
    }

    // Create the shell geometry
    const shellGeometry = new THREE.BufferGeometry();
    shellGeometry.setAttribute('position', new THREE.Float32BufferAttribute(shellPositions, 3));
    shellGeometry.setIndex(shellIndices);

    // Apply Laplacian smoothing to outer surface if enabled
    // NOTE: Only smooth non-boundary vertices to preserve rim shape
    const thicknessState = useThicknessStore.getState().thickness;
    if (thicknessState?.outerSurfaceSmoothing?.enabled && thicknessState.outerSurfaceSmoothing.iterations > 0) {
      try {
        // Build adjacency for the shell geometry BEFORE smoothing
        const neighbors = buildAdjacency(shellGeometry);
        const isBoundary = detectBoundaryVertices(shellGeometry);

        // Collect outer vertex indices (non-boundary vertices only for smoothing)
        const outerVertexIndices = [];
        for (const [origIdx, newIdx] of outerVertexMap.entries()) {
          // Only add non-boundary vertices - boundary vertices should never be smoothed
          if (!boundaryVertices.has(origIdx)) {
            outerVertexIndices.push(newIdx);
          }
        }
        // Note: We explicitly exclude boundary vertices from the smoothing list
        // This ensures the rim shape is preserved and never affected by smoothing

        if (outerVertexIndices.length > 0) {
          // Apply smoothing to outer vertices (boundary vertices will be skipped internally)
          applySurfaceSmoothing(
            shellGeometry,
            outerVertexIndices,
            neighbors,
            isBoundary,
            thicknessState.outerSurfaceSmoothing.iterations
          );
        }
      } catch (error) {
        console.warn('[ThicknessHandler] Failed to apply outer surface smoothing:', error);
      }
    }

    // Handle vertex colors if they exist in the base geometry
    if (baseGeometry.attributes.color) {
      const originalColors = baseGeometry.attributes.color.array;
      const shellColors = [];

      // Add colors for outer vertices (non-boundary)
      for (let i = 0; i < vertexCount; i++) {
        if (!boundaryVertices.has(i)) {
          const i3 = i * 3;
          shellColors.push(originalColors[i3], originalColors[i3 + 1], originalColors[i3 + 2]);
        }
      }

      // Add colors for outer boundary vertices
      for (let i = 0; i < vertexCount; i++) {
        if (boundaryVertices.has(i)) {
          const i3 = i * 3;
          shellColors.push(originalColors[i3], originalColors[i3 + 1], originalColors[i3 + 2]);
        }
      }

      // Add colors for inner vertices (non-boundary)
      for (let i = 0; i < vertexCount; i++) {
        if (!boundaryVertices.has(i)) {
          const i3 = i * 3;
          shellColors.push(originalColors[i3], originalColors[i3 + 1], originalColors[i3 + 2]);
        }
      }

      // Add colors for inner boundary vertices
      for (let i = 0; i < vertexCount; i++) {
        if (boundaryVertices.has(i)) {
          const i3 = i * 3;
          shellColors.push(originalColors[i3], originalColors[i3 + 1], originalColors[i3 + 2]);
        }
      }

      // Add colors for side wall vertices
      for (let i = 0; i < vertexCount; i++) {
        if (boundaryVertices.has(i)) {
          const i3 = i * 3;
          // Side wall outer vertex
          shellColors.push(originalColors[i3], originalColors[i3 + 1], originalColors[i3 + 2]);
          // Side wall inner vertex
          shellColors.push(originalColors[i3], originalColors[i3 + 1], originalColors[i3 + 2]);
        }
      }

      shellGeometry.setAttribute('color', new THREE.Float32BufferAttribute(shellColors, 3));
    }

    // Compute normals for proper lighting
    shellGeometry.computeVertexNormals();
    shellGeometry.computeBoundingSphere();

    return shellGeometry;
  }

  function generateIndicesForNonIndexed(vertexCount) {
    const indices = [];
    for (let i = 0; i < vertexCount; i++) {
      indices.push(i);
    }
    return indices;
  }

  function findBoundaryEdges(indices) {
    // Find edges that appear only once (boundary edges) with proper orientation
    const edgeCount = new Map();
    const orientedEdges = new Map(); // Store oriented edge info

    for (let i = 0; i < indices.length; i += 3) {
      const a = indices[i];
      const b = indices[i + 1];
      const c = indices[i + 2];

      // Store edges with their original orientation from the face
      const edges = [
        [a, b], // edge from vertex a to vertex b
        [b, c], // edge from vertex b to vertex c
        [c, a]  // edge from vertex c to vertex a
      ];

      edges.forEach(([v1, v2]) => {
        const minV = Math.min(v1, v2);
        const maxV = Math.max(v1, v2);
        const key = `${minV}-${maxV}`;
        const isForward = v1 < v2; // true if edge goes from smaller to larger vertex index

        if (!edgeCount.has(key)) {
          edgeCount.set(key, 0);
          orientedEdges.set(key, { forward: 0, backward: 0 });
        }

        edgeCount.set(key, edgeCount.get(key) + 1);
        const edgeInfo = orientedEdges.get(key);
        if (isForward) {
          edgeInfo.forward++;
        } else {
          edgeInfo.backward++;
        }
      });
    }

    // Return boundary edges with consistent orientation
    const boundaryEdges = [];
    for (const [key, count] of edgeCount) {
      if (count === 1) {
        const [a, b] = key.split('-').map(Number);
        const edgeInfo = orientedEdges.get(key);

        // Use the actual orientation that appeared in the original face
        if (edgeInfo.forward > 0) {
          boundaryEdges.push([a, b]); // forward direction
        } else {
          boundaryEdges.push([b, a]); // backward direction
        }
      }
    }

    return boundaryEdges;
  }

  function createSideWalls(positions, indices, boundaryEdges, originalVertexCount) {
    // For each boundary edge, create a quad connecting inner and outer surfaces
    boundaryEdges.forEach(([a, b]) => {
      // Outer surface vertices
      const outerA = a;
      const outerB = b;

      // Inner surface vertices (offset by original vertex count)
      const innerA = a + originalVertexCount;
      const innerB = b + originalVertexCount;

      // Create two triangles forming a quad with correct outward-facing normals
      // The quad should face outward from the shell volume
      //
      // Quad layout:
      // outerA ---- outerB
      //   |           |
      //   |           |
      // innerA ---- innerB
      //
      // Triangle 1: outerA -> outerB -> innerB (clockwise from outside)
      indices.push(outerA, outerB, innerB);

      // Triangle 2: outerA -> innerB -> innerA (clockwise from outside)
      indices.push(outerA, innerB, innerA);
    });
  }

  function createSideWallsWithDuplicatedVertices(indices, boundaryEdges, sideWallOuterMap, sideWallInnerMap) {
    // For each boundary edge, create a quad using dedicated side wall vertices
    boundaryEdges.forEach(([a, b]) => {
      // Get dedicated side wall vertices for this edge
      const outerA = sideWallOuterMap.get(a);
      const outerB = sideWallOuterMap.get(b);
      const innerA = sideWallInnerMap.get(a);
      const innerB = sideWallInnerMap.get(b);

      // Create two triangles forming a quad with correct outward-facing normals
      // The quad should face outward from the shell volume
      //
      // Quad layout:
      // outerA ---- outerB
      //   |           |
      //   |           |
      // innerA ---- innerB
      //
      // Triangle 1: outerA -> innerA -> outerB (counter-clockwise from outside)
      indices.push(outerA, innerA, outerB);

      // Triangle 2: outerB -> innerA -> innerB (counter-clockwise from outside)
      indices.push(outerB, innerA, innerB);
    });
  }

  // Component lifecycle
  useEffect(() => {
    return () => {
      disposePreview();
      disposeTopMeshPreview();
      disposeTopMeshSolid();
      disposeTopLidPreview();
      disposeTopLidSolid();
    };
  }, []);

  // Detect unapply and reset base references for fresh calculation
  useEffect(() => {
    // Only run unapply logic if this is not the initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    // If thickness was just unapplied, reset base references to recalculate from current mesh
    if (!isThicknessApplied && scene && shadedMesh) {
      baseGeomRef.current = null;
      basePosRef.current = null;
      baseNormalsRef.current = null;
      baseMaxThicknessRef.current = null;
      lastMeshRef.current = null;
      originalMaterialRef.current = null;
      topMeshRef.current = null;
      lastSocketTypeRef.current = null;
      topLidRef.current = null; // Reset lid cache

      // Force recalculation on next ensurePreview call
      if (previewMeshRef.current) {
        disposePreview();
      }
      if (topMeshPreviewRef.current) {
        disposeTopMeshPreview();
      }
      if (topLidPreviewRef.current) {
        disposeTopLidPreview();
      }
      // Remove any attached solid meshes to restore original shaded mesh
      disposeTopMeshSolid();
      disposeTopLidSolid();
    }
  }, [isThicknessApplied, scene, shadedMesh]);

  // Create preview on mount / shadedMesh changes
  useEffect(() => {
    if (!scene || !shadedMesh) return;

    // Don't create previews if thickness is already applied
    if (isThicknessApplied) return;

    // Bottom mesh preview
    const preview = ensurePreview();

    // Top mesh preview (for mould mode)
    const topPreview = ensureTopMeshPreview();
    
    // Top lid preview (for mould mode)
    const lidPreview = ensureTopLidPreview();

    // Set initial visibility based on current state and non-zero thickness
    const state = useThicknessStore.getState().thickness;
    const previewEnabled = !!state.previewVisible;
    const mmValue = parseFloat(state?.value ?? 0) || 0;
    const topMmValue = parseFloat(state?.topValue ?? 0) || 0;
    if (preview) preview.visible = previewEnabled && mmValue > 0;
    if (topPreview) topPreview.visible = previewEnabled && topMmValue > 0;
    if (lidPreview) lidPreview.visible = previewEnabled && topMmValue > 0;

    rebuildPreview();
    rebuildTopMeshPreview(); // Also rebuild top mesh preview on initial load
    rebuildTopLidPreview(); // Also rebuild top lid preview on initial load

    return () => {
      // Clean up THREE.js objects when dependencies change (but don't apply)
      // The actual cleanup is handled by the main unmount effect
    };
  }, [scene, shadedMesh, selectedSocketType, isThicknessApplied]);

  // Rebuild preview when user changes the thickness or offset value (mm)
  useEffect(() => {
    if (!scene || !shadedMesh || isThicknessApplied) return;
    rebuildPreview();
  }, [thicknessState?.value, thicknessState?.offset, isThicknessApplied]);

  // Rebuild top mesh preview when user changes the top thickness value (mm)
  useEffect(() => {
    if (!scene || !shadedMesh || selectedSocketType !== 'mould' || isThicknessApplied) return;
    rebuildTopMeshPreview();
  }, [thicknessState?.topValue, selectedSocketType, isThicknessApplied]);

  // If shaded mesh changes while mounted, reset bases and rebuild
  useEffect(() => {
    if (lastMeshRef.current !== shadedMesh && scene && shadedMesh && !isThicknessApplied) {
      baseGeomRef.current = null;
      baseMaxThicknessRef.current = null;
      topMeshRef.current = null; // Reset top mesh cache
      topLidRef.current = null; // Reset lid cache
      ensurePreview();
      rebuildPreview();
      // Keep solid top and lid in mould mode, remove otherwise
      if (selectedSocketType === 'mould') {
        ensureTopMeshSolid();
        ensureTopLidSolid();
      } else {
        disposeTopMeshSolid();
        disposeTopLidSolid();
      }
    }
  }, [shadedMesh, isThicknessApplied]);

  // Preview mesh visibility follows previewVisible state and non-zero thickness
  useEffect(() => {
    if (!previewMesh || isThicknessApplied) return;
    const previewEnabled = !!thicknessState?.previewVisible;
    const mmValue = parseFloat(thicknessState?.value ?? 0) || 0;
    previewMesh.visible = previewEnabled && mmValue > 0;
  }, [thicknessState?.previewVisible, previewMesh, isThicknessApplied]);

  // Top mesh visibility follows previewVisible state and non-zero top thickness
  useEffect(() => {
    if (!topMeshPreviewRef.current || isThicknessApplied) return;
    const previewEnabled = !!thicknessState?.previewVisible;
    const topMmValue = parseFloat(thicknessState?.topValue ?? 0) || 0;
    topMeshPreviewRef.current.visible = previewEnabled && topMmValue > 0;
  }, [thicknessState?.previewVisible, thicknessState?.topValue, isThicknessApplied]);

  // Top lid visibility follows previewVisible state and non-zero top thickness
  useEffect(() => {
    if (!topLidPreviewRef.current || isThicknessApplied) return;
    const previewEnabled = !!thicknessState?.previewVisible;
    const topMmValue = parseFloat(thicknessState?.topValue ?? 0) || 0;
    topLidPreviewRef.current.visible = previewEnabled && topMmValue > 0;
  }, [thicknessState?.previewVisible, thicknessState?.topValue, isThicknessApplied]);

  return null;
};

export default ThicknessHandler;